import * as React from 'react';
import { Table } from 'react-bootstrap';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import { compose } from 'redux';

import Loading from 'af-root/scenes/Company/ScheduleBoard/Shared/LoadingModal';

import type { LatestNotificationsByEmployeeViewModel } from 'ab-viewModels/latestNotificationsForEmployees.viewModel';
import type { NotificationStatusByEmployee, NotificationStatusByTemporaryEmployee } from 'ab-viewModels/notification.viewModel';
import type ScheduleBoardEmployee from 'ab-viewModels/scheduleBoardEmployee.viewModel';
import type ScheduleBoardTemporaryEmployee from 'ab-viewModels/scheduleBoardTemporaryEmployee.viewModel';
import type { WorkOrderResourceLookupModel } from 'ab-viewModels/scheduleBoardWorkOrderModal.viewModel';
import type WorkOrderModalViewModel from 'ab-viewModels/scheduleBoardWorkOrderModal.viewModel';
import type { WorkOrderViewModel } from 'ab-viewModels/workOrder.viewModel';
import type WorkOrderResourceLookupViewModel from 'ab-viewModels/workOrderResourceLookup.viewModel';
import type { WorkOrderRevisionEmployee, WorkOrderRevisionTemporaryEmployee } from 'ab-viewModels/workOrderRevisionItem.viewModel';

import { createExistsMap } from '@acceligentllc/shared/utils/array';
import * as TimeUtils from '@acceligentllc/shared/utils/time';
import * as TimeOptionUtils from '@acceligentllc/shared/utils/timeOption';

import { AutoNotifyOption } from '@acceligentllc/shared/enums/autoNotifyOption';
import { DefaultColor } from '@acceligentllc/shared/enums/color';
import type { ExtendedColorValue } from '@acceligentllc/shared/enums/color';
import { NotificationStatusEnum } from '@acceligentllc/shared/enums/notificationStatus';
import { SubscriptionStatus } from '@acceligentllc/shared/enums/subscriptionStatus';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';

import * as ColorUtils from 'af-utils/color.util';
import { useToggle } from 'af-utils/react.util';

import { NOTIFY_EMPLOYEE_TYPE, DAILY_TIP_MAX_LENGTH } from 'af-constants/values';

import { Button } from '@acceligentllc/storybook';

import Checkbox from 'af-components/Controls/Checkbox';
import CustomModal from 'af-components/CustomModal';
import Textarea from 'af-components/Fields/PlainTextArea';
import EmployeeList from 'af-components/NotifyModal/EmployeeList';
import TableHeader from 'af-components/NotifyModal/TableHeader';
import TemporaryEmployeeList from 'af-components/NotifyModal/TemporaryEmployeeList';
import SubmitButton from 'af-components/SubmitButton';

import { getNotifyModalEmployeeFromEmployee, getNotifyModalTemporaryEmployeeFromTemporaryEmployee } from 'af-models/employeeNotifyModal.model';
import type { EmployeeNotifyModalState, TempEmployeeNotifyModalState, EmployeeNotificationStatusDict, TemporaryEmployeeNotificationStatusDict } from 'af-models/employeeNotifyModal.model';

import * as EmployeeActions from 'af-actions/employee';

const SMS_LENGTH_LIMIT = 1550; // it's 1600 but just to be safe we'll set it to 1550

interface OwnProps {
	order: WorkOrderModalViewModel | WorkOrderViewModel;
	participantsEmailAndSmsStatus: NotificationStatusByEmployee;
	temporaryParticipantsEmailAndSmsStatus: Nullable<NotificationStatusByTemporaryEmployee>;
	scheduleBoardEmployees: Nullable<{ [employeeId: number]: ScheduleBoardEmployee; }>;
	scheduleBoardTemporaryEmployees: Nullable<{ [employeeId: number]: ScheduleBoardTemporaryEmployee; }>;
	revisionEmployees: { [revisionId: number]: WorkOrderRevisionEmployee[]; };
	revisionTemporaryEmployees: { [revisionId: number]: WorkOrderRevisionTemporaryEmployee[]; };
	workOrderEmployees: { [workOrderEmployeeId: number]: number; };
	workOrderTemporaryEmployees: { [workOrderTemporaryEmployeeId: number]: number; };
	showModal: boolean;
	isLoading?: boolean;
	autoNotifyOnPublish: Nullable<AutoNotifyOption>;
	enableScheduledNotifications: boolean;
	autoNotifyAt: Nullable<number>;
	notifyTemporaryLabor: boolean;
	dailyTip: string;
	notificationTemplate: string;
	closeModal: () => void;
	notifyParticipants: (
		workOrderEmployeeIdsForSms: number[],
		workOrderEmployeeIdsForEmail: number[],
		workOrderTemporaryEmployeeIdsForSms: number[],
		workOrderEmployeeTemporaryIdsForEmail: number[],
		workOrderId: number,
		message?: string,
		isEdited?: boolean
	) => Promise<void>;
	scheduleAutoNotify: (
		event: {
			workOrderId: number;
			workOrderEmployeeIdsForSms: number[];
			workOrderEmployeeIdsForEmail: number[];
			workOrderTemporaryEmployeeIdsForSms: number[];
			workOrderEmployeeTemporaryIdsForEmail: number[];
			message?: string;
			/** Map of employee ids -> work order ids removed from WO */
			removedEmployeeSMSIdsMap?: { [employeeId: number]: number; };
			/** Map of employee ids -> work order ids removed from WO */
			removedEmployeeEmailIdsMap?: { [employeeId: number]: number; };
			/** Map of employee ids -> work order ids removed from WO */
			removedTemporaryEmployeeSMSIdsMap?: { [employeeId: number]: number; };
			/** Map of employee ids -> work order ids removed from WO */
			removedTemporaryEmployeeEmailIdsMap?: { [employeeId: number]: number; };
		}
	) => Promise<void>;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

interface NotificationIdsReducer {
	workOrderAssignmentEmailIds: number[];
	workOrderAssignmentSMSIds: number[];
	removedAssignmentEmailIdsMap: { [employeeId: number]: number; };
	removedAssignmentSMSIdsMap: { [employeeId: number]: number; };
}

const notificationsByTypeReducer = (workOrderId: number) => {
	return (acc: NotificationIdsReducer, [employeeId, workOrderAssignment]: [string, EmployeeNotifyModalState | TempEmployeeNotifyModalState]) => {
		if (workOrderAssignment.shouldSendEmail) {
			if (!workOrderAssignment.isAssignedToWO) {
				acc.removedAssignmentEmailIdsMap[Number(employeeId)] = workOrderId;
			} else {
				acc.workOrderAssignmentEmailIds.push(workOrderAssignment.id);
			}
		}
		if (workOrderAssignment.shouldSendSms) {
			if (!workOrderAssignment.isAssignedToWO) {
				acc.removedAssignmentSMSIdsMap[Number(employeeId)] = workOrderId;
			} else {
				acc.workOrderAssignmentSMSIds.push(workOrderAssignment.id);
			}
		}
		return acc;
	};
};

type NotificationIdsMap = { sendEmailToIds: number[]; sendSMSToIds: number[]; };

const _notificationEmployeeIdReducer = (acc: NotificationIdsMap, [employeeId, employee]: [string, EmployeeNotifyModalState]) => {
	if (employee.shouldSendEmail) {
		acc.sendEmailToIds.push(Number(employeeId));
	}
	if (employee.shouldSendSms) {
		acc.sendSMSToIds.push(Number(employeeId));
	}
	return acc;
};

const _notificationTemporaryEmployeeIdReducer = (acc: NotificationIdsMap, [temporaryEmployeeId, temporaryEmployee]: [string, TempEmployeeNotifyModalState]) => {
	if (temporaryEmployee.shouldSendEmail) {
		acc.sendEmailToIds.push(Number(temporaryEmployeeId));
	}
	if (temporaryEmployee.shouldSendSms) {
		acc.sendSMSToIds.push(Number(temporaryEmployeeId));
	}
	return acc;
};

const NotifyParticipantsModal: React.FC<Props> = (props) => {

	const {
		order,
		participantsEmailAndSmsStatus,
		temporaryParticipantsEmailAndSmsStatus,
		dailyTip,
		scheduleBoardEmployees,
		scheduleBoardTemporaryEmployees = {},
		workOrderEmployees,
		workOrderTemporaryEmployees,
		closeModal,
		notifyParticipants,
		scheduleAutoNotify,
		notificationTemplate,
		autoNotifyAt,
		autoNotifyOnPublish,
		enableScheduledNotifications,
		notifyTemporaryLabor,
		showModal,
		isLoading,
		revisionEmployees,
		revisionTemporaryEmployees,
		findNotificationStatusForRevisionEmployees,
	} = props;

	const [
		employees,
		setEmployees,
	] = React.useState<EmployeeNotificationStatusDict>({});

	const [
		temporaryEmployees,
		setTemporaryEmployees,
	] = React.useState<TemporaryEmployeeNotificationStatusDict>({});

	const [
		message,
		setMessage,
	] = React.useState<string>('');

	const [
		revisionEmployeesNotifications,
		setRevisionEmployeesNotifications,
	] = React.useState<Nullable<LatestNotificationsByEmployeeViewModel>>(null);

	const {
		value: isSending,
		setToTrue: beginSending,
		setToFalse: endSending,
	} = useToggle(false);

	const {
		value: isResend,
		setToTrue: setIsResend,
		setToFalse: setIsNotResend,
	} = useToggle(false);

	const {
		value: includeTip,
		toggle: toggleIncludeTip,
	} = useToggle(true);

	const {
		value: isMessageEdited,
		setToTrue: setMessageIsEdited,
		setToFalse: setMessageIsNotEdited,
	} = useToggle(false);

	const workOrderId = React.useMemo(() => ('workOrderId' in order
		? order.workOrderId ?? order.id
		: order.id
	), [order]);

	// these employees are the ones that only appear in previous revisions of work order, and are not currently assigned to it
	// and we only take info from last revision they appear in
	const revisionEmployeesLastVersion = React.useMemo(() => {
		const workOrderEmployeesIds = order.workOrderResourceLookups.map(
			(_worl: WorkOrderResourceLookupViewModel | WorkOrderResourceLookupModel) => _worl.employeeId
		);

		const employeeExistsInWorkOrder = createExistsMap(workOrderEmployeesIds, (_id) => _id!);

		return (
			Object.keys(revisionEmployees).reduce<{ [employeeId: number]: WorkOrderRevisionEmployee & { revisionId: number; }; }>(
				(_relv, _revisionId) => {
					const currentRevisionEmployees: WorkOrderRevisionEmployee[] = revisionEmployees[+_revisionId];

					currentRevisionEmployees.forEach((_revEmp) => {
						// if user is not currently in Work Order
						// and current revision of employee is greater than one in dict
						if (
							!employeeExistsInWorkOrder[_revEmp.employeeId] && // emp not on current wo version
							(
								!_relv[_revEmp.employeeId] // we haven't added to accumulator
								|| _relv[_revEmp.employeeId].revisionId < +_revisionId // we have add it but there is newer revision for that emp
							)
						) {
							_relv[_revEmp.employeeId] = {
								..._revEmp,
								revisionId: +_revisionId,
							};
						}
					});
					return _relv;
				}, {})
		);
	}, [order, revisionEmployees]);

	// these temp employees are the ones that only appear in previous revisions of work order, and are not currently assigned to it
	// and we only take info from last revision they appear in
	const revisionTemporaryEmployeesLastVersion = React.useMemo(() => {
		const workOrderTemporaryEmployeesIds = order.workOrderResourceLookups.map((_worl) => _worl.temporaryEmployeeId);

		const temporaryEmployeeExistsInWorkOrder = createExistsMap(workOrderTemporaryEmployeesIds, (_id) => _id!);

		return (
			Object.keys(revisionTemporaryEmployees).reduce<{ [temporaryEmployeeId: number]: WorkOrderRevisionTemporaryEmployee & { revisionId: number; }; }>(
				(_relv, _revisionId) => {
					const currentRevisionTemporaryEmployees: WorkOrderRevisionTemporaryEmployee[] = revisionTemporaryEmployees[+_revisionId];

					currentRevisionTemporaryEmployees.forEach((_revEmp) => {
						// if user is not currently in Work Order
						// and current revision of temporary employee is greater than one in dict
						if (
							!temporaryEmployeeExistsInWorkOrder[_revEmp.temporaryEmployeeId] && // emp not on current wo version
							(
								!_relv[_revEmp.temporaryEmployeeId] // we haven't added to accumulator
								|| _relv[_revEmp.temporaryEmployeeId].revisionId < +_revisionId // we have add it but there is newer revision for that emp
							)
						) {
							_relv[_revEmp.temporaryEmployeeId] = {
								..._revEmp,
								revisionId: +_revisionId,
							};
						}
					});
					return _relv;
				}, {})
		);
	}, [order, revisionTemporaryEmployees]);

	React.useEffect(() => {
		let isMounted = true;

		const getRevisionEmployeesNotifications = async () => {
			const employeesIds = Object.values(revisionEmployeesLastVersion).map((_emp) => _emp.employeeId);

			const _notifications = await findNotificationStatusForRevisionEmployees(employeesIds, workOrderId);

			if (isMounted) {
				setRevisionEmployeesNotifications(_notifications);
			}
		};

		getRevisionEmployeesNotifications();

		return () => {
			isMounted = false;
		};
	}, [revisionEmployeesLastVersion, order, findNotificationStatusForRevisionEmployees, workOrderId]);

	React.useEffect(() => {
		if (!showModal) {
			return;
		}
		let employeeMap: Nullable<EmployeeNotificationStatusDict> = null;
		let tempEmployeeMap: Nullable<TemporaryEmployeeNotificationStatusDict> = null;

		let newIsResend = false;

		if (!order?.workOrderResourceLookups) {
			return;
		}

		const {
			employeeMap: _employeeMap,
			tempEmployeeMap: _tempEmployeeMap,
		} = order.workOrderResourceLookups.reduce((_acc, _worl: WorkOrderResourceLookupModel) => {
			if (_worl.workOrderEmployeeId && _worl.employeeId) {
				const existingEmployee = employees?.[_worl.employeeId];
				if (
					(existingEmployee?.shouldSendEmail && existingEmployee?.lastEmailSentAt)
					|| (existingEmployee?.shouldSendSms && existingEmployee?.lastSmsSentAt)
				) {
					newIsResend = true;
				}
				const employee = scheduleBoardEmployees?.[workOrderEmployees?.[_worl.workOrderEmployeeId]];
				if (!employee) {
					return _acc;
				}
				_acc.employeeMap[_worl.employeeId] = {
					...getNotifyModalEmployeeFromEmployee(
						_worl.workOrderEmployeeId,
						employee,
						participantsEmailAndSmsStatus?.[_worl.workOrderEmployeeId]
					),
					shouldSendEmail: existingEmployee?.email ? existingEmployee.shouldSendEmail : false,
					shouldSendSms: existingEmployee?.phoneNumber ? existingEmployee.shouldSendSms : false,
				};
			} else if (notifyTemporaryLabor && (_worl.workOrderTemporaryEmployeeId && _worl.temporaryEmployeeId)) {
				const existingEmployee = temporaryEmployees?.[_worl.temporaryEmployeeId];
				if (
					(existingEmployee?.shouldSendEmail && existingEmployee?.lastEmailSentAt)
					|| (existingEmployee?.shouldSendSms && existingEmployee?.lastSmsSentAt)
				) {
					newIsResend = true;
				}
				const tempEmployee = scheduleBoardTemporaryEmployees?.[workOrderTemporaryEmployees?.[_worl.workOrderTemporaryEmployeeId]];
				if (!tempEmployee) {
					return _acc;
				}
				_acc.tempEmployeeMap[_worl.temporaryEmployeeId] = {
					...getNotifyModalTemporaryEmployeeFromTemporaryEmployee(
						_worl.workOrderTemporaryEmployeeId,
						tempEmployee,
						temporaryParticipantsEmailAndSmsStatus?.[_worl.workOrderTemporaryEmployeeId] ?? null
					),
					shouldSendEmail: existingEmployee?.email ? existingEmployee.shouldSendEmail : false,
					shouldSendSms: existingEmployee?.phoneNumber ? existingEmployee.shouldSendSms : false,
				};
			}
			return _acc;
		}, {
			employeeMap: {},
			tempEmployeeMap: {},
		} as {
			employeeMap: EmployeeNotificationStatusDict;
			tempEmployeeMap: TemporaryEmployeeNotificationStatusDict;
		});
		employeeMap = _employeeMap;
		tempEmployeeMap = _tempEmployeeMap;

		// include employees removed from latest revisions
		Object.keys(revisionEmployeesLastVersion).forEach((employeeId) => {
			const newEmployee: WorkOrderRevisionEmployee & { revisionId: number; } = revisionEmployeesLastVersion[+employeeId];
			if (employeeMap && !employeeMap[employeeId] && newEmployee) {
				const _employeeNotificationStatus = revisionEmployeesNotifications?.[+employeeId];

				employeeMap[employeeId] = {
					id: +employeeId,
					fullName: newEmployee.fullName,
					phoneNumber: newEmployee.phoneNumber,
					email: newEmployee.email,
					officeColor: newEmployee.officeColor,
					officeNickname: newEmployee.officeNickname,
					lastEmailSentStatus: _employeeNotificationStatus?.emailStatus,
					lastSmsSentStatus: _employeeNotificationStatus?.smsStatus,
					lastEmailSentAt: _employeeNotificationStatus?.emailSentAt,
					lastSmsSentAt: _employeeNotificationStatus?.smsSentAt,
					emailInvalidatedAt: undefined,
					phoneInvalidatedAt: undefined,
					subscriptionStatus: newEmployee.subscriptionStatus ?? SubscriptionStatus.SUBSCRIBED,
					subscribedForEmail: newEmployee.emailStatus === NotificationStatusEnum.SCHEDULED,
					subscribedForSms: newEmployee.smsStatus === NotificationStatusEnum.SCHEDULED,
					emailErrorMessage: _employeeNotificationStatus?.emailErrorMessage,
					smsErrorMessage: _employeeNotificationStatus?.smsErrorMessage,
					shouldSendEmail: false,
					shouldSendSms: false,
					isAssignedToWO: false,
				} as EmployeeNotifyModalState;
			}
		});

		// include temporary employees removed from latest revisions
		Object.keys(revisionTemporaryEmployeesLastVersion).forEach((temporaryEmployeeId) => {
			const newTemporaryEmployee: WorkOrderRevisionTemporaryEmployee & { revisionId: number; }
				= revisionTemporaryEmployeesLastVersion[+temporaryEmployeeId];
			if (tempEmployeeMap && !tempEmployeeMap[temporaryEmployeeId] && newTemporaryEmployee) {
				const _employeeNotificationStatus = revisionEmployeesNotifications?.[+temporaryEmployeeId];

				tempEmployeeMap[temporaryEmployeeId] = {
					id: +temporaryEmployeeId,
					fullName: newTemporaryEmployee.fullName,
					phoneNumber: newTemporaryEmployee.phoneNumber,
					email: newTemporaryEmployee.email,
					lastEmailSentStatus: _employeeNotificationStatus?.emailStatus,
					lastSmsSentStatus: _employeeNotificationStatus?.smsStatus,
					lastEmailSentAt: _employeeNotificationStatus?.emailSentAt,
					lastSmsSentAt: _employeeNotificationStatus?.smsSentAt,
					emailInvalidatedAt: undefined,
					phoneInvalidatedAt: undefined,
					subscriptionStatus: newTemporaryEmployee.subscriptionStatus ?? SubscriptionStatus.SUBSCRIBED,
					subscribedForEmail: newTemporaryEmployee.emailStatus === NotificationStatusEnum.SCHEDULED,
					subscribedForSms: newTemporaryEmployee.smsStatus === NotificationStatusEnum.SCHEDULED,
					emailErrorMessage: _employeeNotificationStatus?.emailErrorMessage,
					smsErrorMessage: _employeeNotificationStatus?.smsErrorMessage,
					shouldSendEmail: false,
					shouldSendSms: false,
					isAssignedToWO: false,
				} as TempEmployeeNotifyModalState;
			}
		});

		setEmployees(employeeMap);
		if (tempEmployeeMap) {
			setTemporaryEmployees(tempEmployeeMap);
		}
		newIsResend ? setIsResend() : setIsNotResend();
		setMessage(isMessageEdited ? message : dailyTip);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		dailyTip,
		isMessageEdited,
		order,
		participantsEmailAndSmsStatus,
		temporaryParticipantsEmailAndSmsStatus,
		revisionEmployees,
		scheduleBoardEmployees,
		workOrderEmployees,
		revisionEmployeesNotifications,
		revisionEmployeesLastVersion,
		setIsResend,
		setIsNotResend,
	]); // do not add employees and message to dependency array as linter suggests

	const resetState = React.useCallback(async () => {
		setEmployees({});
		setTemporaryEmployees({});
		endSending();
		setIsNotResend();
		setMessage(dailyTip);
	}, [dailyTip, endSending, setIsNotResend]);

	const onCancel = React.useCallback(() => {
		resetState();
		closeModal();
	}, [closeModal, resetState]);

	const onSubmit = React.useCallback(async () => {
		beginSending();
		const {
			sendEmailToIds: employeeEmailIds,
			sendSMSToIds: employeeSmsIds,
		} = Object.entries(employees).reduce(_notificationEmployeeIdReducer, { sendEmailToIds: [], sendSMSToIds: [] });

		const {
			sendEmailToIds: tempEmployeeEmailIds,
			sendSMSToIds: tempEmployeeSmsIds,
		} = Object.entries(temporaryEmployees).reduce(_notificationTemporaryEmployeeIdReducer, { sendEmailToIds: [], sendSMSToIds: [] });

		const _message = includeTip && message.length ? message : undefined;
		await notifyParticipants(employeeSmsIds, employeeEmailIds, tempEmployeeSmsIds, tempEmployeeEmailIds, workOrderId, _message, isMessageEdited);
		resetState();
	}, [beginSending, employees, temporaryEmployees, includeTip, message, notifyParticipants, workOrderId, isMessageEdited, resetState]);

	const checkAll = React.useCallback(async (value: boolean, type: NOTIFY_EMPLOYEE_TYPE) => {
		let newIsResend = false;
		const newEmployees = Object.entries(employees).reduce((_acc, [_eId, _e]) => {
			if (
				(NOTIFY_EMPLOYEE_TYPE.EMAIL && value && _e.email && _e.lastEmailSentAt)
				|| (NOTIFY_EMPLOYEE_TYPE.SMS && value && _e.phoneNumber && _e.lastSmsSentAt)
			) {
				newIsResend = true;
			}
			_acc[_eId] = {
				..._e,
				shouldSendEmail: type === NOTIFY_EMPLOYEE_TYPE.EMAIL ? (_e.email && value) : _e.shouldSendEmail,
				shouldSendSms: type === NOTIFY_EMPLOYEE_TYPE.SMS ? (_e.phoneNumber && value) : _e.shouldSendSms,
			};
			return _acc;
		}, {});

		const newTempEmployees = Object.entries(temporaryEmployees).reduce((_acc, [_teId, _te]) => {
			if (
				(NOTIFY_EMPLOYEE_TYPE.EMAIL && value && _te.email && _te.lastEmailSentAt)
				|| (NOTIFY_EMPLOYEE_TYPE.SMS && value && _te.phoneNumber && _te.lastSmsSentAt)
			) {
				newIsResend = true;
			}
			_acc[_teId] = {
				..._te,
				shouldSendEmail: type === NOTIFY_EMPLOYEE_TYPE.EMAIL ? (_te.email && value) : _te.shouldSendEmail,
				shouldSendSms: type === NOTIFY_EMPLOYEE_TYPE.SMS ? (_te.phoneNumber && value) : _te.shouldSendSms,
			};
			return _acc;
		}, {});

		setEmployees(newEmployees);
		setTemporaryEmployees(newTempEmployees);
		newIsResend ? setIsResend() : setIsNotResend();
	}, [employees, setIsNotResend, setIsResend, temporaryEmployees]);

	const onEmployeeCheck = React.useCallback(async (workOrderEmployeeId: number, type: NOTIFY_EMPLOYEE_TYPE) => {

		const employeeId = Object.keys(employees).find((_employeeId) => employees[_employeeId]?.id === workOrderEmployeeId);
		if (employeeId) {
			setEmployees({
				...employees,
				[employeeId]: {
					...employees[employeeId],
					shouldSendEmail: type === NOTIFY_EMPLOYEE_TYPE.EMAIL
						? !employees[employeeId].shouldSendEmail
						: employees[employeeId].shouldSendEmail,
					shouldSendSms: type === NOTIFY_EMPLOYEE_TYPE.SMS
						? !employees[employeeId].shouldSendSms
						: employees[employeeId].shouldSendSms,
				},
			});

			!!Object.values(employees).find((_e) => (_e.shouldSendEmail && !!_e.lastEmailSentAt) || (_e.shouldSendEmail && !!_e.lastSmsSentAt))
				? setIsResend()
				: setIsNotResend();
		}
	}, [employees, setIsNotResend, setIsResend]);

	const onTempEmployeeCheck = React.useCallback(async (workOrderTemporaryEmployeeId: number, type: NOTIFY_EMPLOYEE_TYPE) => {

		const temporaryEmployeeId = Object.keys(temporaryEmployees)
			.find((_temporaryEmployeeId) => temporaryEmployees[_temporaryEmployeeId]?.id === workOrderTemporaryEmployeeId);
		if (temporaryEmployeeId) {
			setTemporaryEmployees({
				...temporaryEmployees,
				[temporaryEmployeeId]: {
					...temporaryEmployees[temporaryEmployeeId],
					shouldSendEmail: type === NOTIFY_EMPLOYEE_TYPE.EMAIL
						? !temporaryEmployees[temporaryEmployeeId].shouldSendEmail
						: temporaryEmployees[temporaryEmployeeId].shouldSendEmail,
					shouldSendSms: type === NOTIFY_EMPLOYEE_TYPE.SMS
						? !temporaryEmployees[temporaryEmployeeId].shouldSendSms
						: temporaryEmployees[temporaryEmployeeId].shouldSendSms,
				},
			});

			!!Object.values(temporaryEmployees).find((_e) => (_e.shouldSendEmail && !!_e.lastEmailSentAt) || (_e.shouldSendEmail && !!_e.lastSmsSentAt))
				? setIsResend()
				: setIsNotResend();
		}
	}, [temporaryEmployees, setIsNotResend, setIsResend]);

	const onSchedule = React.useCallback(async () => {
		beginSending();

		const {
			workOrderAssignmentEmailIds: workOrderEmployeeEmailIds,
			workOrderAssignmentSMSIds: workOrderEmployeeSMSIds,
			removedAssignmentEmailIdsMap: removedEmployeeEmailIdsMap,
			removedAssignmentSMSIdsMap: removedEmployeeSMSIdsMap,
		} = Object.entries(employees).reduce<NotificationIdsReducer>(
			notificationsByTypeReducer(workOrderId),
			{ workOrderAssignmentEmailIds: [], workOrderAssignmentSMSIds: [], removedAssignmentEmailIdsMap: {}, removedAssignmentSMSIdsMap: {} }
		);

		const {
			workOrderAssignmentEmailIds: workOrderTemporaryEmployeeEmailIds,
			workOrderAssignmentSMSIds: workOrderTemporaryEmployeeSMSIds,
			removedAssignmentEmailIdsMap: removedTemporaryEmployeeEmailIdsMap,
			removedAssignmentSMSIdsMap: removedTemporaryEmployeeSMSIdsMap,
		} = Object.entries(temporaryEmployees).reduce<NotificationIdsReducer>(
			notificationsByTypeReducer(workOrderId),
			{ workOrderAssignmentEmailIds: [], workOrderAssignmentSMSIds: [], removedAssignmentEmailIdsMap: {}, removedAssignmentSMSIdsMap: {} }
		);

		const _message = includeTip && isMessageEdited && message.length ? message : undefined;
		await scheduleAutoNotify(
			{
				workOrderId,
				workOrderEmployeeIdsForEmail: workOrderEmployeeEmailIds,
				workOrderEmployeeIdsForSms: workOrderEmployeeSMSIds,
				workOrderEmployeeTemporaryIdsForEmail: workOrderTemporaryEmployeeEmailIds,
				workOrderTemporaryEmployeeIdsForSms: workOrderTemporaryEmployeeSMSIds,
				message: _message,
				removedEmployeeEmailIdsMap,
				removedEmployeeSMSIdsMap,
				removedTemporaryEmployeeEmailIdsMap,
				removedTemporaryEmployeeSMSIdsMap,
			}
		);
		resetState();
	}, [beginSending, employees, workOrderId, temporaryEmployees, includeTip, isMessageEdited, message, scheduleAutoNotify, resetState]);

	const editMessage = React.useCallback((newMessage: string) => {
		if (newMessage.length < DAILY_TIP_MAX_LENGTH) {
			setMessage(newMessage);
			newMessage !== dailyTip ? setMessageIsEdited() : setMessageIsNotEdited();
		}
	}, [dailyTip, setMessageIsEdited, setMessageIsNotEdited]);

	const renderLoadingModalContent = React.useCallback(() => (
		<Loading onHide={onCancel} />
	), [onCancel]);

	const renderOrderHeader = React.useCallback((code: string, title: string, customer: string, color: ExtendedColorValue) => {
		return (
			<tr className={`order-header ${ColorUtils.getColorBackgroundClass(color)}`}>
				<td colSpan={100}>
					<span>{title} | {code} | {customer}</span>
				</td>
			</tr>
		);
	}, []);

	const getCrewTypeInfo = React.useCallback(() => {
		return ('crewTypeColor' in order) ? order.crewTypeColor : order.crewType.color;
	}, [order]);

	const submitDisabled = React.useMemo(() => {
		return [...Object.values(employees), ...Object.values(temporaryEmployees)].every(((_e) => !_e.shouldSendEmail && !_e.shouldSendSms));
	}, [employees, temporaryEmployees]);

	const {
		emailNumber,
		smsNumber,
	} = React.useMemo(() => {
		return [...Object.values(employees), ...Object.values(temporaryEmployees)].reduce((_acc, _emp) => {
			if (_emp.shouldSendEmail) {
				_acc.emailNumber += 1;
			}
			if (_emp.shouldSendSms) {
				_acc.smsNumber += 1;
			}
			return _acc;
		}, { emailNumber: 0, smsNumber: 0 });
	}, [employees, temporaryEmployees]);

	const renderDailyTipForm = React.useCallback((): React.JSX.Element => (
		<div className="content-form">
			<Checkbox
				handleChange={toggleIncludeTip}
				inline={true}
				isChecked={includeTip}
				label={<span className="m-l-xs">Include Custom Message with Notification (daily tip is used by default)</span>}
			/>
			<Textarea
				disabled={!includeTip}
				maxCharacters={DAILY_TIP_MAX_LENGTH}
				onValueChange={editMessage}
				placeholder="Enter Message"
				rows={2}
				showMaxCharactersLabel={true}
				value={message}
			/>
		</div>
	), [editMessage, includeTip, message, toggleIncludeTip]);

	const renderWarning = React.useCallback(() => {
		const tip = message.length ? message : dailyTip;
		const _message = includeTip ? `${notificationTemplate} ${tip}` : notificationTemplate;

		if (_message?.length <= SMS_LENGTH_LIMIT) {
			return null;
		}

		return (
			<div className="text-red">
				Message could exceed 1600 characters and SMS may not be sent. Try reducing daily tip or work order notes.
			</div>
		);
	}, [dailyTip, includeTip, message, notificationTemplate]);

	const renderDataModalContent = React.useCallback(() => {
		const {
			title,
			code,
			dueDate,
			customerName,
			customCrewType,
			status: workOrderStatus,
		} = order;

		const sendLabel = isResend ? 'Resend' : 'Send';
		const dateBefore = TimeUtils.formatDate(TimeUtils.parseMoment(dueDate, TimeFormat.DATE_ONLY)?.subtract(1, 'day'), TimeFormat.DAY_OF_WEEK_WITH_DATE_AND_YEAR);

		const now = new Date();
		const currentDate = TimeUtils.formatDate(now, TimeFormat.DAY_OF_WEEK_WITH_DATE_AND_YEAR);
		const currentTime = now.getHours() * 60 + now.getMinutes();

		const timeToNotifyPassed = !!TimeUtils.isBefore(dateBefore, currentDate, TimeFormat.DAY_OF_WEEK_WITH_DATE_AND_YEAR)
			|| (currentDate === dateBefore && autoNotifyAt && currentTime > autoNotifyAt);

		const showScheduleButton = enableScheduledNotifications && autoNotifyAt && !timeToNotifyPassed;

		return (
			<>
				<CustomModal.Header
					closeModal={onCancel}
					title="Notify Participants"
				/>
				<CustomModal.Body>
					<Table>
						<TableHeader checkAll={checkAll} showSubscribedColumns={autoNotifyOnPublish !== AutoNotifyOption.DEFAULT_TIME} />
						<tbody>
							{renderOrderHeader(code, title ?? '', customerName ?? '', !customCrewType ? getCrewTypeInfo() ?? DefaultColor.INTERNAL_JOB : DefaultColor.INTERNAL_JOB)}
							<EmployeeList
								employees={employees}
								isSending={isSending}
								onCheck={onEmployeeCheck}
								showSubscribedColumns={autoNotifyOnPublish !== AutoNotifyOption.DEFAULT_TIME}
								subscribedAt={`${dateBefore}, ${TimeOptionUtils.format(autoNotifyAt)}`}
							/>
							<TemporaryEmployeeList
								employees={temporaryEmployees}
								isSending={isSending}
								onCheck={onTempEmployeeCheck}
								showSubscribedColumns={autoNotifyOnPublish !== AutoNotifyOption.DEFAULT_TIME}
								subscribedAt={`${dateBefore}, ${TimeOptionUtils.format(autoNotifyAt)}`}
							/>
						</tbody>
					</Table>
				</CustomModal.Body>
				<div className="daily-tip-modal resizable-container">
					{workOrderStatus !== WorkOrderStatus.CANCELED && renderDailyTipForm()}
					<div className="content-footer">
						<div className="question">
							Send <b><span className="text-orange">{emailNumber}</span></b> e-mail notifications
							and <b><span className="text-orange">{smsNumber}</span></b> SMS notifications
							to laborers of work order <b>{code}</b> of job <b>{title}</b>?
						</div>
						{autoNotifyOnPublish === AutoNotifyOption.DEFAULT_TIME &&
							<div>Automatic notifications will be sent at {TimeOptionUtils.format(autoNotifyAt)}, {dateBefore}</div>
						}
						{renderWarning()}
					</div>
				</div>
				<CustomModal.Footer>
					<Button
						label="Close"
						onClick={onCancel}
						style="secondary"
					/>
					<SubmitButton
						disabled={submitDisabled}
						label={sendLabel}
						onClick={onSubmit}
						reduxFormSubmitting={isSending}
					/>
					{showScheduleButton &&
						<SubmitButton
							disabled={submitDisabled}
							label={`Notify at ${dateBefore}, ${TimeOptionUtils.format(autoNotifyAt)}`}
							onClick={onSchedule}
							reduxFormSubmitting={isSending}
						/>
					}
				</CustomModal.Footer>
			</>
		);
	}, [
		autoNotifyAt,
		autoNotifyOnPublish,
		checkAll,
		employees,
		getCrewTypeInfo,
		isResend,
		isSending,
		onCancel,
		onEmployeeCheck,
		onTempEmployeeCheck,
		onSchedule,
		onSubmit,
		order,
		renderDailyTipForm,
		renderOrderHeader,
		renderWarning,
		temporaryEmployees,
		submitDisabled,
		emailNumber,
		smsNumber,
		enableScheduledNotifications,
	]);

	if (!order || !showModal) {
		return null;
	}

	return (
		<CustomModal
			closeModal={onCancel}
			modalStyle="info"
			showModal={showModal}
			size="xl"
		>
			{(isLoading && !isSending) ? renderLoadingModalContent() : renderDataModalContent()}
		</CustomModal>
	);
};

const mapDispatchToProps = () => ({
	findNotificationStatusForRevisionEmployees: EmployeeActions.findLatestNotificationsForEmployeesOnWorkOrder,
});

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	connector,
	React.memo
);

export default enhance(NotifyParticipantsModal);
